"use client";

import { useRef, useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  INPUT,
  INPUT_MONO,
  LABEL,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  LETTER_SCALE_PRESETS,
  type LetterScalePreset,
  letterFor,
} from "@/lib/tool-kit/grades/letter-scales";
import {
  proficiencyLetter,
  type RubricCriterion,
  type RubricResult,
  scoreRubric,
} from "@/lib/tool-kit/grades/rubric";

/** English strings. A translated content file overrides these through `ui`. */
export const RUBRIC_SCORE_CALCULATOR_STRINGS = {
  scaleLabel: "Rubric scale",
  scaleOption: "{min} to {max}",
  letterScaleLabel: "Letter scale for the percentage",
  scalePlusMinus: "Plus/minus — A 93, A− 90, B+ 87 … D− 60",
  scalePlusMinusAPlus: "Plus/minus with A+ from 97",
  scaleTenPoint: "10-point A–F — A 90, B 80, C 70, D 60",
  criteriaHeading: "Criteria",
  criterionLabel: "Criterion",
  criterionPlaceholder: "Criterion {n}",
  scoreLabel: "Score",
  weightLabel: "Weight",
  removeCriterion: "Remove",
  removeCriterionAria: "Remove criterion {n}",
  addCriterion: "Add a criterion",
  results: "Result",
  pointsEarned: "Points earned",
  pointsOf: "{earned} of {possible}",
  linearPercent: "Percentage (points ÷ possible)",
  linearLetter: "Letter from the percentage",
  average: "Average rubric score",
  averageOf: "{average} of {max}",
  proficiencyLetter: "Letter by proficiency conversion",
  proficiencyOnlyFour:
    "The proficiency conversion (A = 3.00–4.00, B = 2.50–2.99, C = 2.00–2.49, D = 1.00–1.99) is defined for 4-point scales.",
  invalid:
    "Every score must be a number from {min} to {max}, and every weight 0 or more, with at least one weight above 0.",
  comparisonHeading: "What each level becomes",
  colLevel: "Every criterion at",
  colLinear: "Percentage",
  colLinearLetter: "Letter (percentage)",
  colProficiency: "Letter (proficiency)",
  comparisonNote:
    "The lowest level still earns {floor}% of the points, so a percentage made this way can never fall below {floor}% — and a level that means “meets the standard” can land in the C or D range.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "Rubric score: {earned} of {possible} points ({percent}%), average {average} of {max}",
} as const;

type Strings = typeof RUBRIC_SCORE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

const RUBRIC_SCALES = ["1-4", "0-4", "1-3", "1-5", "1-6"] as const;
type RubricScale = (typeof RUBRIC_SCALES)[number];

function scaleBounds(scale: RubricScale): { min: number; max: number } {
  const [min, max] = scale.split("-").map(Number);
  return { min, max };
}

interface CriterionRow {
  id: number;
  name: string;
  score: string;
  weight: string;
}

const INITIAL_ROWS: CriterionRow[] = [
  { id: 1, name: "", score: "3", weight: "1" },
  { id: 2, name: "", score: "3", weight: "1" },
  { id: 3, name: "", score: "4", weight: "1" },
  { id: 4, name: "", score: "2", weight: "1" },
];

/** The criteria as numbers, or null when any score or weight is out of range. */
function readCriteria(
  rows: readonly CriterionRow[],
  min: number,
  max: number
): RubricCriterion[] | null {
  const criteria: RubricCriterion[] = [];
  for (const row of rows) {
    const score = parseNumberInput(row.score);
    const weight = parseNumberInput(row.weight);
    if (score === null || weight === null) {
      return null;
    }
    if (score < min || score > max || weight < 0) {
      return null;
    }
    criteria.push({ score, weight });
  }
  return criteria;
}

function ResultPanel({
  result,
  max,
  letterScale,
  strings,
  fmt,
}: {
  result: RubricResult;
  max: number;
  letterScale: LetterScalePreset;
  strings: Strings;
  fmt: Format;
}) {
  const two = (value: number) =>
    fmt(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const short = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const proficiency = proficiencyLetter(result.average, max);
  const linear = letterFor(result.percent, LETTER_SCALE_PRESETS[letterScale]);
  return (
    <>
      <StatGrid>
        <StatCard
          label={strings.pointsEarned}
          value={t(strings.pointsOf, {
            earned: short(result.earned),
            possible: short(result.possible),
          })}
        />
        <StatCard
          label={strings.linearPercent}
          value={`${short(result.percent)}%`}
        />
        <StatCard label={strings.linearLetter} value={linear?.letter ?? "—"} />
        <StatCard
          label={strings.average}
          value={t(strings.averageOf, {
            average: two(result.average),
            max: short(max),
          })}
        />
        <StatCard
          emphasis
          label={strings.proficiencyLetter}
          value={proficiency ?? "—"}
        />
      </StatGrid>
      {proficiency === null ? (
        <Notice>{strings.proficiencyOnlyFour}</Notice>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, {
          earned: short(result.earned),
          possible: short(result.possible),
          percent: short(result.percent),
          average: two(result.average),
          max: short(max),
        })}
      />
    </>
  );
}

function LevelComparison({
  min,
  max,
  letterScale,
  strings,
  fmt,
}: {
  min: number;
  max: number;
  letterScale: LetterScalePreset;
  strings: Strings;
  fmt: Format;
}) {
  const levels: number[] = [];
  for (let level = max; level >= min; level -= 1) {
    levels.push(level);
  }
  const short = (value: number) => fmt(value, { maximumFractionDigits: 1 });
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.comparisonHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colLevel}
              </th>
              <th className={TH} scope="col">
                {strings.colLinear}
              </th>
              <th className={TH} scope="col">
                {strings.colLinearLetter}
              </th>
              <th className={TH} scope="col">
                {strings.colProficiency}
              </th>
            </tr>
          </thead>
          <tbody>
            {levels.map((level) => {
              const percent = (level * 100) / max;
              const linear = letterFor(
                percent,
                LETTER_SCALE_PRESETS[letterScale]
              );
              return (
                <tr key={level}>
                  <td className={TD}>{fmt(level)}</td>
                  <td className={TD}>{`${short(percent)}%`}</td>
                  <td className={TD}>{linear?.letter ?? "—"}</td>
                  <td className={TD}>{proficiencyLetter(level, max) ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {min > 0 ? (
        <p className="text-muted-foreground text-xs">
          {t(strings.comparisonNote, { floor: short((min * 100) / max) })}
        </p>
      ) : null}
    </section>
  );
}

export default function RubricScoreCalculatorWidget() {
  const strings = useToolUi(RUBRIC_SCORE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const idCounter = useRef(INITIAL_ROWS.length);
  const [scale, setScale] = useState<RubricScale>("1-4");
  const [letterScale, setLetterScale] =
    useState<LetterScalePreset>("plus-minus");
  const [rows, setRows] = useState<CriterionRow[]>(INITIAL_ROWS);

  const { min, max } = scaleBounds(scale);
  const criteria = readCriteria(rows, min, max);
  const result = criteria === null ? null : scoreRubric(criteria, max);

  function update(id: number, patch: Partial<CriterionRow>) {
    setRows((all) =>
      all.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    idCounter.current += 1;
    const id = idCounter.current;
    setRows((all) => [
      ...all,
      { id, name: "", score: String(max), weight: "1" },
    ]);
  }

  return (
    <div className="space-y-6">
      <div className={`${CARD} grid gap-4 sm:grid-cols-2`}>
        <SelectField
          id="rsc-scale"
          label={strings.scaleLabel}
          onChange={setScale}
          options={RUBRIC_SCALES.map((value) => ({
            value,
            label: t(strings.scaleOption, scaleBounds(value)),
          }))}
          value={scale}
        />
        <SelectField
          id="rsc-letters"
          label={strings.letterScaleLabel}
          onChange={setLetterScale}
          options={[
            { value: "plus-minus", label: strings.scalePlusMinus },
            { value: "plus-minus-a-plus", label: strings.scalePlusMinusAPlus },
            { value: "ten-point", label: strings.scaleTenPoint },
          ]}
          value={letterScale}
        />
      </div>

      <div className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.criteriaHeading}</h3>
        {rows.map((row, index) => (
          <div className="flex flex-wrap items-end gap-3" key={row.id}>
            <div className="min-w-0 flex-1 basis-48 space-y-1">
              <label className={LABEL} htmlFor={`rsc-name-${row.id}`}>
                {strings.criterionLabel}
              </label>
              <input
                className={INPUT}
                id={`rsc-name-${row.id}`}
                onChange={(event) =>
                  update(row.id, { name: event.target.value })
                }
                placeholder={t(strings.criterionPlaceholder, { n: index + 1 })}
                type="text"
                value={row.name}
              />
            </div>
            <div className="w-24 space-y-1">
              <label className={LABEL} htmlFor={`rsc-score-${row.id}`}>
                {strings.scoreLabel}
              </label>
              <input
                className={INPUT_MONO}
                id={`rsc-score-${row.id}`}
                inputMode="decimal"
                onChange={(event) =>
                  update(row.id, { score: event.target.value })
                }
                type="text"
                value={row.score}
              />
            </div>
            <div className="w-24 space-y-1">
              <label className={LABEL} htmlFor={`rsc-weight-${row.id}`}>
                {strings.weightLabel}
              </label>
              <input
                className={INPUT_MONO}
                id={`rsc-weight-${row.id}`}
                inputMode="decimal"
                onChange={(event) =>
                  update(row.id, { weight: event.target.value })
                }
                type="text"
                value={row.weight}
              />
            </div>
            <button
              aria-label={t(strings.removeCriterionAria, { n: index + 1 })}
              className={BUTTON_SMALL}
              disabled={rows.length <= 1}
              onClick={() =>
                setRows((all) => all.filter((r) => r.id !== row.id))
              }
              type="button"
            >
              {strings.removeCriterion}
            </button>
          </div>
        ))}
        <button className={BUTTON_SECONDARY} onClick={addRow} type="button">
          {strings.addCriterion}
        </button>
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result === null ? (
          <Notice>
            {t(strings.invalid, { min: fmt(min), max: fmt(max) })}
          </Notice>
        ) : (
          <ResultPanel
            fmt={fmt}
            letterScale={letterScale}
            max={max}
            result={result}
            strings={strings}
          />
        )}
      </section>

      <LevelComparison
        fmt={fmt}
        letterScale={letterScale}
        max={max}
        min={min}
        strings={strings}
      />
    </div>
  );
}
